/* eslint-disable react-hooks/exhaustive-deps */
import { COUNTRY_FLAG_ICON } from '@configs/language/config';
import {
  Currencies,
  DecimalDigits,
  ExtendedCurrencies,
  WalletTypes,
} from '@constants/enums';
import { SelectOption } from '@efex-components/common/types';
import { Button, TextField, Tooltip } from '@efexteam/components';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import InfoIcon from '@mui/icons-material/Info';
import PaidIcon from '@mui/icons-material/Paid';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import {
  Alert,
  AlertTitle,
  FormControl,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRole, ROLES } from '@efexteam/permission-manager';
import { useApiCall } from 'use-api-call';
import { Country } from '../../../../configs/language';
import { NEW_USER_PERSON } from '../../../../constants/clients';
import useCurrencyBalance from '../../../../hooks/useCurrencyBalance';
import { DEBOUNCE_TIME } from '../../../../hooks/useDebounce';
import { PromoterRateInput } from '../components/PromoterRateInput';
import {
  selectBusiness,
  selectProfileData,
} from '../../../../pages/profile/store/selectors';
import rateSvc from '../../../../services/rate/rateSvc';
import {
  ExchangeResponse,
  FeeDescription,
  IExchange,
} from '../../../../services/rate/types';
import { getExchangeResult } from '../../../../utils/convertion';
import executeWithCancellation from '../../../../utils/promiseUtils';
import {
  camelCaseMapData,
  formatCurrency,
  getCurrencySymbol,
} from '../../../../utils/formater';
import {
  applyEfexRateRounding,
  validateDecimalInput,
} from '../utils/promoterRateHelpers';
import {
  isPaymentBlocked,
  getBlockedPaymentReasonKey,
  showChinaPaymentWarning,
} from '../../../../utils/paymentValidation';
import Select from '../../../common/Form/Select';
import {
  COUNTRY_CURRENCY,
  CURRENCY_ICON,
  CURRENCY_OPTIONS,
} from '../../../common/Form/config';
import NewPaymentModalSkeleton from '../../../common/Skeleton/NewPaymentModalSkeleton';
import {
  AMOUNT_MONEY,
  CURRENCY_CODES,
  RATE_ROUNDING_DECIMALS,
} from '../constants/catalogs';
import { useFormData } from '../context';

interface AmountProps {
  nextFormStep: () => void;
  prevFormStep: () => void;
}

type FormData = {
  amount: number;
  isReverse: boolean;
};

const Amount = ({ nextFormStep, prevFormStep }: AmountProps) => {
  const { t } = useTranslation();
  const company = useSelector(selectBusiness);
  const cancelFunctionRef = useRef<(() => void) | undefined>();
  const cancelFeeFunctionRef = useRef<(() => void) | undefined>();

  const { setFormValues, data } = useFormData();

  const { client: clientSelected } = data;

  const [currency, setCurrency] = useState(
    company?.accountCurrency || COUNTRY_CURRENCY[company?.country as Country]
  );
  const clientCurrency = useMemo(
    () =>
      clientSelected?.currency ||
      COUNTRY_CURRENCY[clientSelected?.country as Country],
    [clientSelected]
  );

  const [timerExchange, setTimerExchange] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [timerFee, setTimerFee] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [enteredAmount, setEnteredAmount] = useState<string>();
  const [amountDataPayment, setAmountDataPayment] = useState<string>();
  const [feeDescription, setFeeDescription] = useState<
    FeeDescription | undefined
  >();
  const [isPromoterRateValid, setIsPromoterRateValid] = useState<boolean>(true);

  const profile = useSelector(selectProfileData);
  const { roles } = useRole();
  const isPromoter = roles?.includes(ROLES.PROMOTER);
  const promoterId = profile?.userPromoter?.[0]?.promoterId;

  const { balance: currencyBalance, wallet } = useCurrencyBalance({
    currency,
  });

  const { setValue } = useForm<FormData>();

  const showPromoterRateInput = useMemo(
    () => currency !== clientCurrency && isPromoter,
    [isPromoter, currency, clientCurrency]
  );

  const {
    data: exchangeData,
    loading,
    error,
    invoke: getExchange,
  } = useApiCall(() =>
    rateSvc
      .getExchange({
        currency,
        desCurrency: clientCurrency,
        enterpriseId: company?.enterpriseId || '0',
      })
      .then((response) =>
        camelCaseMapData<ExchangeResponse, IExchange>(response)
      )
  );

  const handleCancel = () => {
    [timerExchange, timerFee].forEach((timer) => {
      if (timer) {
        clearTimeout(timer);
      }
    });

    [cancelFunctionRef, cancelFeeFunctionRef].forEach((cancelRef) => {
      if (cancelRef.current) {
        cancelRef.current();
        cancelRef.current = undefined;
      }
    });
  };

  const updateAmountAndFormValues = (
    calculatedAmount: string,
    isReverse: boolean,
    updates: Partial<typeof data>,
    responseFeeDescription?: any
  ) => {
    const formattedAmount = parseFloat(calculatedAmount || '0').toFixed(2);

    if (isReverse) {
      setEnteredAmount(formattedAmount);
    } else {
      setAmountDataPayment(formattedAmount);
    }

    setFormValues({
      ...data,
      clientCurrency,
      resultAmount: formattedAmount,
      ...updates,
    });

    if (responseFeeDescription) {
      setFeeDescription(responseFeeDescription);
    }
  };

  const handlePromoterRateChange = (params: {
    rate: number;
    calculatedAmount: string;
    isReverse: boolean;
    feeDescription?: any;
    activeRateUuid?: string | null;
  }) => {
    const {
      rate,
      calculatedAmount,
      isReverse,
      feeDescription: responseFeeDescription,
      activeRateUuid: newActiveRateUuid,
    } = params;

    const updates = {
      finalExchangeRate: rate,
      feeDescription: responseFeeDescription,
      activeRateUuid: newActiveRateUuid,
      isPromoter,
    };

    updateAmountAndFormValues(
      calculatedAmount,
      isReverse,
      updates,
      responseFeeDescription
    );
  };

  const handlePromoterRateClear = (params: {
    calculatedAmount: string;
    isReverse: boolean;
    feeDescription?: any;
    activeRateUuid?: string | null;
  }) => {
    const {
      calculatedAmount,
      isReverse,
      feeDescription: responseFeeDescription,
    } = params;

    const updates = {
      activeRateUuid: null,
      finalExchangeRate: undefined,
    };

    updateAmountAndFormValues(
      calculatedAmount,
      isReverse,
      updates,
      responseFeeDescription
    );
  };

  const handlePromoterRateValidationChange = (isValid: boolean) => {
    setIsPromoterRateValid(isValid);
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const isReverse = name === 'sendAmount';
    const fixedAmount = validateDecimalInput(value);
    const amount = fixedAmount;
    setFormValues({
      ...data,
      clientCurrency,
      amount: undefined,
      reverse: isReverse,
    });
    handleCancel();

    if (Number.isNaN(amount)) {
      return;
    }

    setFormValues({
      ...data,
      amount,
      reverse: isReverse,
      currency,
      clientCurrency,
    });
    if (amount === '') {
      setAmountDataPayment(amount);
      setEnteredAmount(amount);
      return;
    }

    if (currency === clientCurrency) {
      const feeTimer = setTimeout(async () => {
        const feeResult = await rateSvc.getFeeResult({
          currency,
          reverse: isReverse,
          amount,
          contactId:
            clientSelected?.contactId != null
              ? Number(clientSelected?.contactId)
              : undefined,
        });

        cancelFeeFunctionRef.current = feeResult.cancel;

        const response = await executeWithCancellation(() => feeResult.request);

        if (response) {
          const { result, feeDescription: feeResponse } = response;

          const resultAmount =
            parseFloat(result) > AMOUNT_MONEY.ZERO_NUMBER
              ? result
              : AMOUNT_MONEY.ZERO;

          if (!isReverse) {
            setAmountDataPayment(resultAmount);
          } else {
            setEnteredAmount(resultAmount);
          }
          setFeeDescription(feeResponse);
          setFormValues({
            ...data,
            amount,
            reverse: isReverse,
            resultAmount,
            feeDescription: feeResponse,
          });
        }
      }, DEBOUNCE_TIME);

      if (timerFee) clearTimeout(timerFee);
      setTimerFee(feeTimer);
      return;
    }
    if (isReverse) {
      setAmountDataPayment(amount);
    } else {
      setEnteredAmount(amount);
    }

    const timer = setTimeout(() => {
      const effectiveRateUuid =
        isPromoter && showPromoterRateInput && data?.activeRateUuid
          ? data?.activeRateUuid
          : data?.rateData?.reference ?? '';

      getExchangeResult({
        amount: amount.toString(),
        rateUuid: effectiveRateUuid,
        reverse: isReverse,
        enterpriseId: company?.enterpriseId || '0',
        requestFee: true,
        onChangeResult: ({ amount: result, feeDescription: feeResponse }) => {
          if (!isReverse) {
            setAmountDataPayment(result.toString());
          } else {
            setEnteredAmount(result.toString());
          }
          setFeeDescription(feeResponse);
          setFormValues({
            ...data,
            amount,
            reverse: isReverse,
            resultAmount: result.toString(),
            feeDescription: feeResponse,
          });
        },
        setCancelFunction: (cancel) => {
          cancelFunctionRef.current = cancel;
        },
      });
    }, DEBOUNCE_TIME);

    if (timerExchange) clearTimeout(timerExchange);
    setTimerExchange(timer);
  };

  useEffect(() => {
    const { amount, reverse } = data;
    if (amount) {
      if (reverse) {
        setAmountDataPayment(amount);
      } else {
        setEnteredAmount(amount);
      }
    }
    if (reverse) {
      setValue('isReverse', reverse);
    }
  }, [data, setValue]);

  useEffect(() => {
    if (exchangeData) {
      setFormValues({
        ...data,
        clientCurrency,
        rateData: exchangeData,
        exchangeData: {
          currency,
          clientCurrency,
          exchangeData,
        },
        isPromoter,
      });
    }
  }, [exchangeData]);

  useEffect(() => {
    setFeeDescription(undefined);
    if (clientCurrency !== currency) {
      getExchange();
    }
  }, [clientCurrency, currency]);

  useEffect(() => {
    setAmountDataPayment('0');
    setEnteredAmount('0');
    setFormValues({ ...data, currency, amount: '0', resultAmount: '0' });
  }, [currency]);

  const isPromoterRateInputEnabled = useMemo(() => {
    const hasValidOriginAmount =
      enteredAmount && enteredAmount !== AMOUNT_MONEY.ZERO;
    const hasValidDestinationAmount =
      amountDataPayment && amountDataPayment !== AMOUNT_MONEY.ZERO;
    return Boolean(hasValidOriginAmount || hasValidDestinationAmount);
  }, [enteredAmount, amountDataPayment]);

  const currentRateAmount = useMemo(() => {
    if (!exchangeData?.buy) return '0';
    const baseRate = parseFloat(exchangeData.buy);
    return applyEfexRateRounding(baseRate, exchangeData.isMainCurrency).toFixed(
      RATE_ROUNDING_DECIMALS
    );
  }, [exchangeData]);

  const isAirwallexPayment = useMemo(
    () =>
      Number(amountDataPayment) < 1 &&
      [Currencies.USD, ExtendedCurrencies.EUR].includes(clientCurrency),
    [amountDataPayment, clientCurrency]
  );

  const isBlockedPayment = useMemo(
    () => isPaymentBlocked(clientCurrency, clientSelected?.country_iso || ''),
    [clientCurrency, clientSelected?.country_iso]
  );

  const isChinaPaymentWarning = useMemo(
    () => showChinaPaymentWarning(currency, clientSelected?.country_iso || ''),
    [currency, clientSelected?.country_iso]
  );

  const blockedPaymentReasonKey = useMemo(
    () =>
      getBlockedPaymentReasonKey(
        clientCurrency,
        clientSelected?.country_iso || ''
      ),
    [clientCurrency, clientSelected?.country_iso]
  );

  const availableWallets = useMemo(
    () =>
      wallet?.filter(
        (walletBalance) =>
          walletBalance.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT &&
          !(
            walletBalance.currency.name === Currencies.COP &&
            clientCurrency === Currencies.COP
          )
      ) ?? [],
    [wallet, clientCurrency]
  );

  if (loading) {
    return <NewPaymentModalSkeleton />;
  }

  if (!clientSelected) return null;

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('transferMoney')}
      </Typography>
      <div className="bg-slate-200 rounded p-4 mb-4 w-full flex justify-start items-start md:justify-around md:items-center max-md:flex-col">
        <div>
          <Typography>{t('clientBalance')}</Typography>
          <Typography variant="h6">
            {(clientSelected.entityBusiness === NEW_USER_PERSON
              ? clientSelected.name
              : clientSelected.companyName) || clientSelected.email}
          </Typography>
        </div>
        <div>
          <Typography>{t('accountBalance')}</Typography>
          <Typography variant="h5" className="!font-bold">
            {currencyBalance} {currency}
          </Typography>
        </div>
      </div>
      {feeDescription?.list_fees && (
        <div className="mb-4">
          {feeDescription.list_fees.map((fee) =>
            fee.amount !== AMOUNT_MONEY.ZERO_NUMBER ? (
              <Alert
                key={fee.strategy_code}
                severity="warning"
                className="mb-2"
              >
                {t(fee.description || '')}
              </Alert>
            ) : null
          )}
        </div>
      )}
      {error && (error as any).response.data && currency !== clientCurrency ? (
        <Alert className="mb-4" severity="error">
          <AlertTitle>{t('errorRetryMessage')}</AlertTitle>
          {t((error as any).response.data.detail)}
        </Alert>
      ) : null}
      {isChinaPaymentWarning && (
        <Alert className="mb-4" severity="warning">
          {t('paymentChinaWarning')}
        </Alert>
      )}
      <div>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Grid container>
              <Typography className="block w-full">
                {t('recipientGets')}
              </Typography>
              <Grid item xs={10}>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    className="border-transparent"
                    id="amountResult"
                    name="amountResult"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(currency)}
                        </InputAdornment>
                      ),
                    }}
                    placeholder="0.00"
                    value={enteredAmount}
                    onChange={handleChange}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <Select
                  className="h-full border-transparent"
                  options={
                    availableWallets?.map((walletBalance) => ({
                      id: walletBalance.currency.name,
                      value: walletBalance.currency.name,
                      icon: CURRENCY_ICON[
                        walletBalance.currency
                          .name as keyof typeof CURRENCY_ICON
                      ],
                      isDisabled:
                        walletBalance.currency.name === Currencies.COP &&
                        clientCurrency === Currencies.COP,
                    })) as unknown as SelectOption[]
                  }
                  defaultValue={
                    CURRENCY_OPTIONS.find((e) => e.id === currency) ||
                    CURRENCY_OPTIONS[0]
                  }
                  onClickOption={(value) => setCurrency(value as Currencies)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className="convert_data">
              <div className="convert_data-type">
                <div className="convert_data-info">
                  <ArrowCircleRightIcon sx={{ color: '#4db6ac' }} />
                  <Typography className="flex row ml-1">
                    {currency !== clientCurrency
                      ? t('amountConver')
                      : t('amountTranfer')}
                    :{' '}
                    <b className="pl-1">
                      {formatCurrency(
                        enteredAmount,
                        DecimalDigits.COMPLETE_DIGITS,
                        DecimalDigits.DEFAULT_MIN_DIGITS,
                        currency
                      )}{' '}
                      {currency}
                    </b>
                  </Typography>
                </div>
                {exchangeData?.exchangeLabel && currency !== clientCurrency ? (
                  <div className="convert_data-info">
                    <SwapHorizontalCircleIcon sx={{ color: '#4db6ac' }} />
                    <Typography
                      className={clsx(
                        'flex row ml-1 gap-2',
                        error && (error as any).response.data
                          ? 'bg-red-50 py-3 px-2 rounded'
                          : ''
                      )}
                      component="div"
                    >
                      {t('realTimeExchange')}:{' '}
                      <Typography component="span" className="m-0 !font-bold">
                        $ {exchangeData?.exchangeLabel}
                      </Typography>
                      <Tooltip
                        title={
                          <>
                            <Typography variant="caption" component="p">
                              {t('walletCalculateDisclaimer')}
                            </Typography>
                            <Typography variant="caption" component="p">
                              {t('walletCalculateDisclaimerMessage', {
                                amount: currentRateAmount,
                                currency:
                                  exchangeData?.exchangeLabel?.split(' ')[
                                    (
                                      exchangeData?.exchangeLabel?.split(' ') ??
                                      []
                                    ).length - 1
                                  ],
                              })}
                            </Typography>
                          </>
                        }
                        placement="top"
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </Typography>
                  </div>
                ) : null}
                {feeDescription?.list_fees &&
                enteredAmount !== AMOUNT_MONEY.ZERO
                  ? feeDescription.list_fees.map((fee) =>
                      fee.amount !== AMOUNT_MONEY.ZERO_NUMBER ? (
                        <div
                          key={fee.strategy_code}
                          className="convert_data-info"
                        >
                          <PaidIcon sx={{ color: '#4db6ac' }} />
                          <Typography
                            className={clsx(
                              'flex row ml-1',
                              error && (error as any).response.data
                                ? 'bg-red-50 py-3 px-2 rounded'
                                : ''
                            )}
                          >
                            {t(fee.name || '')}
                            <Typography
                              component="span"
                              className="m-0 !font-bold"
                            >
                              :{' '}
                              {formatCurrency(
                                fee.amount,
                                DecimalDigits.DEFAULT_DIGITS,
                                DecimalDigits.DEFAULT_MIN_DIGITS,
                                clientCurrency
                              )}{' '}
                              {clientCurrency}
                            </Typography>
                          </Typography>
                        </div>
                      ) : null
                    )
                  : null}
              </div>
            </div>
          </Grid>
          {showPromoterRateInput && (
            <Grid item xs={12}>
              <PromoterRateInput
                currency={currency}
                clientCurrency={clientCurrency}
                exchangeData={exchangeData}
                promoterId={promoterId || ''}
                enterpriseId={company?.enterpriseId || '0'}
                currentAmount={
                  data.reverse ? amountDataPayment || '' : enteredAmount || ''
                }
                isReverse={data.reverse || false}
                rateReference={exchangeData?.reference}
                disabled={!isPromoterRateInputEnabled}
                onRateChange={handlePromoterRateChange}
                onRateClear={handlePromoterRateClear}
                onValidationChange={handlePromoterRateValidationChange}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container>
              <Typography>{t('amountToSend3')}</Typography>
              <Grid item xs={10}>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    className="border-transparent"
                    id="sendAmount"
                    name="sendAmount"
                    error={isAirwallexPayment}
                    helperText={
                      isAirwallexPayment
                        ? t('amountToSendHelper', {
                            currency: clientCurrency,
                          })
                        : null
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(clientCurrency)}
                        </InputAdornment>
                      ),
                    }}
                    value={amountDataPayment}
                    placeholder="0.00"
                    onChange={handleChange}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <span className="input-group-text flex flex-row justify-center items-center p-1">
                  {
                    COUNTRY_FLAG_ICON[
                      (clientCurrency as unknown as keyof typeof COUNTRY_FLAG_ICON) ??
                        CURRENCY_CODES.USD
                    ]
                  }
                  {clientCurrency}
                </span>
              </Grid>
            </Grid>
          </Grid>
          {isBlockedPayment && (
            <Alert severity="error" className="mb-4 mx-0 text-sm md:text-base">
              <Typography
                variant="body2"
                className="text-sm md:text-base leading-relaxed"
              >
                {t('paymentBlockedMessage', {
                  reason: t(blockedPaymentReasonKey || ''),
                })}
              </Typography>
            </Alert>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row !mt-4 gap-4"
        >
          <Button
            appearance="secondary"
            size="large"
            className="mt-4"
            onClick={prevFormStep}
          >
            {t('back')}
          </Button>
          <Button
            disabled={
              isBlockedPayment ||
              isAirwallexPayment ||
              !data.amount ||
              !enteredAmount ||
              enteredAmount === AMOUNT_MONEY.ZERO ||
              !amountDataPayment ||
              amountDataPayment === AMOUNT_MONEY.ZERO ||
              (clientCurrency === Currencies.COP &&
                currency === Currencies.COP) ||
              (showPromoterRateInput && !isPromoterRateValid)
            }
            appearance="primary"
            size="large"
            className="mt-4"
            onClick={nextFormStep}
          >
            {t('continue')}
          </Button>
        </Grid>
      </div>
    </>
  );
};

export default Amount;
